import type {
  DeleteThreatProfileResponse,
  ThreatProfile,
  ThreatProfileBody,
  ThreatProfileResponse,
} from '../../shared/types/index.js';
import { attachAppNames, buildAppNameIndex } from '../domain/appNames.js';
import { request } from './client.js';

/** Threat profile as edited on the client: app bundles carry names, the live count is not part of it. */
export type ThreatProfileInput = Omit<ThreatProfile, 'count'>;

function toBody(profile: ThreatProfileInput): ThreatProfileBody {
  return {
    name: profile.name,
    hostileProps: {
      languages: profile.hostileProps.languages,
      countries: profile.hostileProps.countries,
      appBundles: profile.hostileProps.appBundles.map(app => app.bundle),
    },
  };
}

function fromResponse({ profile, apps }: ThreatProfileResponse): ThreatProfileInput {
  const names = buildAppNameIndex(apps);
  return {
    ...profile,
    hostileProps: { ...profile.hostileProps, appBundles: attachAppNames(profile.hostileProps.appBundles, names) },
  };
}

export async function createProfile(profile: ThreatProfileInput): Promise<ThreatProfileInput> {
  return fromResponse(await request<ThreatProfileResponse>('/profiles', { method: 'POST', body: toBody(profile) }));
}

/** Detaches the profile from every perimeter set and deletes it. */
export async function deleteProfile(id: string): Promise<void> {
  await request<DeleteThreatProfileResponse>(`/profiles/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function updateProfile(id: string, profile: ThreatProfileInput): Promise<ThreatProfileInput> {
  return fromResponse(
    await request<ThreatProfileResponse>(`/profiles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: toBody(profile),
    })
  );
}
